import { PATHS, REQUEST_TIMEOUT_MS } from "../constants";
import { ApiError } from "./types";
import type { SubmitRequest, SubmitResponse, StatusResponse } from "./types";

type SubmitArgs = {
  baseUrl: string;
  body: SubmitRequest;
};

type StatusArgs = {
  baseUrl: string;
  jobId: string;
};

function timedFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  return fetch(url, { ...init, signal: controller.signal }).finally(() =>
    clearTimeout(timer)
  );
}

function toNetworkError(err: unknown): ApiError {
  if (err instanceof DOMException && err.name === "AbortError") {
    return new ApiError("Request timed out", undefined);
  }
  return new ApiError(
    err instanceof Error ? err.message : "Network error",
    undefined
  );
}

export async function submitLead({
  baseUrl,
  body,
}: SubmitArgs): Promise<SubmitResponse> {
  let res: Response;
  try {
    res = await timedFetch(`${baseUrl}${PATHS.submit}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw toNetworkError(err);
  }

  let json: SubmitResponse;
  try {
    json = (await res.json()) as SubmitResponse;
  } catch {
    throw new ApiError(`Unexpected response (HTTP ${res.status})`, res.status);
  }

  if (!res.ok) {
    const message =
      json && "message" in json && json.message
        ? json.message
        : `Request failed (HTTP ${res.status})`;
    throw new ApiError(message, res.status);
  }

  return json;
}

export async function fetchJobStatus({
  baseUrl,
  jobId,
}: StatusArgs): Promise<StatusResponse> {
  const url = `${baseUrl}${PATHS.status}?job_id=${encodeURIComponent(jobId)}`;
  let res: Response;
  try {
    res = await timedFetch(url, { method: "GET" });
  } catch (err) {
    throw toNetworkError(err);
  }

  let json: StatusResponse;
  try {
    json = (await res.json()) as StatusResponse;
  } catch {
    throw new ApiError(`Unexpected response (HTTP ${res.status})`, res.status);
  }

  if (!res.ok) {
    const message =
      json && "message" in json && json.message
        ? json.message
        : `Status check failed (HTTP ${res.status})`;
    throw new ApiError(message, res.status);
  }

  return json;
}
